import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Chart from 'react-apexcharts';
import Loader from '../../../../component/common/Loader';
import { fetchCompanyMoodUserDetail } from './companyMoodApi';
import ContextMixPanel from './ContextMixPanel';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  ProgressList,
  PANEL_ROW_CLASS,
  Chip,
  EntityHeader,
  BackLink,
  STAT_ICONS,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID,
  toneChip
} from '../../../../component/common/designSystem';
import { BAND_RAMP } from './moodTheme';

/**
 * Mood Tracking → User profile.
 *
 * One person's mood log: how consistently they check in, how their check-ins
 * sit across the three emotion bands, which emotions they name most, and the
 * contexts those check-ins happen in.
 *
 * What is deliberately NOT here: the note attached to a check-in, the photo,
 * and the individual check-in rows themselves. The note is encrypted at rest
 * precisely because it is special-category health data, and no dashboard is a
 * good enough reason to decrypt it. A company admin can see that someone tracks
 * their mood and how it trends; the moment-by-moment record of it, and anything
 * they wrote about it, stays between the user and the product.
 *
 * `fetcher`/`backPath`/`backLabel` are props so a super-admin Mood hub can
 * reuse the page against the unscoped endpoint.
 */
const MoodUserDetail = ({ fetcher = fetchCompanyMoodUserDetail, backPath, backLabel }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');

  const [loader, setLoader] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!userId) {
      navigate(backPath, { replace: true });
      return;
    }
    let mounted = true;
    setLoader(true);
    fetcher(userId).then((data) => {
      if (!mounted) return;
      setDetail(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, fetcher]);

  const user = detail?.user;
  const bandSplit = detail?.bandSplit || [];
  const topEmotions = detail?.topEmotions || [];
  const daily = detail?.dailyActivity || [];

  // Oldest-first so the chart reads left to right, while the table below stays
  // most-recent-first the way every other history list in the panel does.
  const chartDays = [...daily].reverse();

  const activityOptions = {
    chart: { ...CHART_BASE, type: 'bar', stacked: true },
    colors: BAND_RAMP,
    plotOptions: { bar: { borderRadius: 3, columnWidth: '55%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: { categories: chartDays.map((day) => day.date) },
    yaxis: { labels: { formatter: (val) => Math.round(val) } },
    tooltip: { shared: true, y: { formatter: (val) => `${val.toLocaleString()} check-ins` } }
  };

  return (
    <div>
      {loader ? <Loader /> : null}

      <BackLink label={backLabel} onClick={() => navigate(backPath)} />

      {user && (
        <EntityHeader
          className="mt-4"
          image={user.user_profile}
          name={user.name}
          subtitle={user.email}
          action={
            <Chip className={toneChip('brand')}>
              {user.accountType === 'B2B' ? `B2B — ${user.company}` : 'B2C'}
            </Chip>
          }
        />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4 mt-6">
        <StatCard icon={STAT_ICONS.logs} title="Check-ins" value={user ? user.checkIns : '-'} />
        <StatCard
          icon={STAT_ICONS.streak}
          title="Days Logged"
          value={user ? user.loggedDays : '-'}
        />
        {/* Blank rather than 0% when they have never logged — "hasn't started"
            and "never had a good day" are very different things. */}
        <StatCard
          icon={STAT_ICONS.rating}
          title="Positive Moods"
          value={user?.checkIns > 0 ? `${user.positiveRate}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.sessions}
          title="Mixed Moods"
          value={user?.checkIns > 0 ? `${user.mixedRate}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.score}
          title="Difficult Moods"
          value={user?.checkIns > 0 ? `${user.difficultRate}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.completions}
          title="Check-ins per Day"
          value={user ? user.avgCheckInsPerDay : '-'}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        <MiniDonutCard
          title="Mood Balance"
          subtitle="Their check-ins, by emotion band"
          data={bandSplit}
          colors={BAND_RAMP}
          unit="check-ins"
          totalLabel="Check-ins"
        />

        <Panel title="Emotions They Name" subtitle="Most often chosen, primary or secondary">
          {topEmotions.length > 0 ? (
            <ProgressList items={topEmotions} unit="check-ins" color={CHART_COLORS.brand} />
          ) : (
            <PanelEmpty>No emotions logged yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Mood History" subtitle="First and most recent check-in">
          <PanelTable
            head={
              <>
                <th className="py-2 pr-4 font-medium">Measure</th>
                <th className="py-2 font-medium text-right">Value</th>
              </>
            }
          >
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">First check-in</td>
              <td className="py-2 text-right">{user?.firstCheckInDate || '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Last check-in</td>
              <td className="py-2 text-right">{user?.lastCheckInDate || '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Days logged</td>
              <td className="py-2 text-right">{user ? user.loggedDays : '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Total check-ins</td>
              <td className="py-2 text-right">{user ? user.checkIns : '-'}</td>
            </tr>
          </PanelTable>
        </Panel>
      </div>

      {/* Their own context mix — the same normalised reading as the dashboard,
          narrowed to five options so a handful of check-ins doesn't sprawl. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <ContextMixPanel
          title="Where They Are"
          subtitle="Their mood mix by location"
          rows={detail?.byLocation || []}
          emptyText="No check-ins yet."
          height={280}
        />
        <ContextMixPanel
          title="What They're Doing"
          subtitle="Their mood mix by activity"
          rows={detail?.byActivity || []}
          emptyText="No check-ins yet."
          height={280}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel title="Recent Activity" subtitle="Check-ins by band, per day logged">
          {chartDays.length > 0 ? (
            <Chart
              type="bar"
              height={320}
              width="100%"
              series={[
                { name: 'Positive', data: chartDays.map((day) => day.green) },
                { name: 'Mixed', data: chartDays.map((day) => day.amber) },
                { name: 'Difficult', data: chartDays.map((day) => day.red) }
              ]}
              options={activityOptions}
            />
          ) : (
            <PanelEmpty>No check-ins yet.</PanelEmpty>
          )}
        </Panel>

        {/* Days, not individual check-ins: the moment-by-moment record is
            deliberately not exposed here. */}
        <Panel title="Check-in Days" subtitle="Most recent days this user logged a mood">
          {daily.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Date</th>
                  <th className="py-2 pr-4 font-medium text-right">Check-ins</th>
                  <th className="py-2 pr-4 font-medium text-right">Positive</th>
                  <th className="py-2 pr-4 font-medium text-right">Mixed</th>
                  <th className="py-2 font-medium text-right">Difficult</th>
                </>
              }
            >
              {daily.map((day) => (
                <tr key={day.date} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">{day.date}</td>
                  <td className="py-2 pr-4 text-right">{day.checkIns}</td>
                  <td className="py-2 pr-4 text-right">{day.green}</td>
                  <td className="py-2 pr-4 text-right">{day.amber}</td>
                  <td className="py-2 text-right">{day.red}</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>No check-ins yet.</PanelEmpty>
          )}
        </Panel>
      </div>
    </div>
  );
};

MoodUserDetail.propTypes = {
  fetcher: PropTypes.func,
  backPath: PropTypes.string.isRequired,
  backLabel: PropTypes.string
};

MoodUserDetail.defaultProps = {
  backLabel: 'Back'
};

export default MoodUserDetail;
